/**
 * OT-0F.1-D + PDP-001V — Global EnvBadge widget.
 *
 * COM-FOUNDATION hardening (PDP-001V Phase 7):
 *   - Reads the canonical FE_BUILD stamp from `feBuildStamp.js`
 *     (regenerated on every supervisor restart by
 *     `/app/scripts/stamp_fe_build.sh`), NOT a never-set env var.
 *   - Reads the live backend identity from
 *     `window.__TREATX_RUNTIME__.backend` (populated by
 *     `runtimeObservability.js`) — that handle is the AUTHORITATIVE
 *     source for "the bundle the browser actually loaded".
 *   - Surfaces parity state (PARITY_OK / FE_STALE / BE_STALE /
 *     RUNTIME_MISMATCH / UNKNOWN / BUNDLE_SOURCE_MISMATCH /
 *     PARTIAL_DEPLOY) so the owner does not need to open devtools to
 *     verify FE/BE alignment.
 *   - Adds runtime/schema epoch rows, instance + startup UUID, and
 *     served-bundle SHA so the owner can rule out CDN / stale-tab
 *     drift independently.
 *
 * Never shows secrets. Hidden when `REACT_APP_HIDE_ENV_BADGE` is
 * truthy. Persists collapsed/dismissed state in localStorage.
 */
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Layers, X, RefreshCcw } from 'lucide-react';
import { FE_BUILD } from '../utils/feBuildStamp';

function hostOf(url) {
  try { return new URL(url).host; } catch (_) { return ''; }
}

// PRODUCTION-OVERLAY-HIDE-01 — auto-hide the EnvBadge on the public
// production host so it can never overlay the Soft Launch Gate
// landing or intercept the Sign-in tap target on mobile. The badge
// remains visible on preview / dev / *.emergentagent.com hosts so
// engineers can still see build / parity state during testing.
function _isProductionHost() {
  if (typeof window === 'undefined') return false;
  const h = (window.location.hostname || '').toLowerCase();
  if (!h) return false;
  return h === 'treatx.health' || h.endsWith('.treatx.health');
}

// PDP-001V Phase 7 — colour key for parity state.  Mirrors the colour
// used by VersionBadge so owner sees the same signal across surfaces.
const PARITY_COLOR = {
  PARITY_OK: { bg: '#22c55e', fg: '#022c22' },
  FE_STALE: { bg: '#f59e0b', fg: '#3f2d0b' },
  BE_STALE: { bg: '#f59e0b', fg: '#3f2d0b' },
  RUNTIME_MISMATCH: { bg: '#ef4444', fg: '#fef2f2' },
  BUNDLE_SOURCE_MISMATCH: { bg: '#ef4444', fg: '#fef2f2' },
  PARTIAL_DEPLOY: { bg: '#ef4444', fg: '#fef2f2' },
  UNKNOWN: { bg: '#64748b', fg: '#e2e8f0' },
};

function _readRuntime() {
  if (typeof window === 'undefined' || !window.__TREATX_RUNTIME__) return null;
  try {
    return window.__TREATX_RUNTIME__.dump();
  } catch (_) {
    return null;
  }
}

function _refreshRuntime() {
  if (typeof window !== 'undefined' && window.__TREATX_RUNTIME__?.checkNow) {
    try { window.__TREATX_RUNTIME__.checkNow(); } catch (_) {}
  }
}

export default function EnvBadge() {
  const hide =
    (process.env.REACT_APP_HIDE_ENV_BADGE || '').toLowerCase() === 'true'
    || _isProductionHost();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('treatx_env_badge_collapsed') !== 'false'; }
    catch (_) { return true; }
  });
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('treatx_env_badge_dismissed') === 'true'; }
    catch (_) { return false; }
  });
  // Live runtime snapshot — refreshed every 3s from the canonical handle.
  const [runtime, setRuntime] = useState(_readRuntime);

  useEffect(() => {
    if (hide || dismissed) return undefined;
    const id = setInterval(() => setRuntime(_readRuntime()), 3000);
    setRuntime(_readRuntime());
    return () => clearInterval(id);
  }, [hide, dismissed]);

  if (hide || dismissed) return null;

  const be = runtime?.backend?.backend || null;
  const beRuntime = runtime?.backend?.runtime || null;
  const parity = runtime?.parity?.state || 'UNKNOWN';
  const parityReason = runtime?.parity?.reason || '';
  const palette = PARITY_COLOR[parity] || PARITY_COLOR.UNKNOWN;
  const backendHost = hostOf(process.env.REACT_APP_BACKEND_URL || '');
  const envName = be?.environment || 'preview';
  const served = runtime?.servedBundle || null;
  const feShaShort = FE_BUILD.sha ? String(FE_BUILD.sha).slice(0, 12) : '—';
  // A SHA carried over from an earlier stamp proves nothing about what is
  // actually deployed, so the badge says so rather than implying parity.
  const feShaVerified = FE_BUILD.revision_verified !== false;
  const feShaDisplay = feShaVerified
    ? feShaShort
    : `${feShaShort} (unverified${FE_BUILD.build_tag ? ` · build ${FE_BUILD.build_tag}` : ''})`;
  const beShaShort = be?.git_hash ? String(be.git_hash).slice(0, 12) : '—';
  const fpHash = runtime?.backend?.fingerprint_hash || '—';
  // PRODUCTION-OVERLAY-WHITESCREEN-FIX-01 — these three display
  // strings were referenced in the expanded panel JSX but never
  // defined. React threw ReferenceError on first expansion, the
  // exception unmounted the parent tree, and the Soft Launch Gate
  // landing went white on mobile. Defined here as plain derivations
  // from the same runtime / FE_BUILD handles the rest of the badge
  // already consumes — no schema change, no new data source.
  const feVersionDisplay = FE_BUILD.version || FE_BUILD.semver || '—';
  const beVersionDisplay = be?.version || be?.semver || '—';
  const beBuildIdDisplay = be?.build_id || be?.build_tag || '—';

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem('treatx_env_badge_collapsed', String(next)); } catch (_) {}
      return next;
    });
  };

  const handleDismiss = () => {
    try { localStorage.setItem('treatx_env_badge_dismissed', 'true'); } catch (_) {}
    setDismissed(true);
  };

  return (
    <div
      // PRE-M-C — bottom-LEFT + shifted UP above VersionBadge so both
      // pills stay clear of the composer's [data-testid=send-btn]
      // (bottom-right). Wrapper is pointer-events:none; inner pill
      // re-enables clicks.
      className="fixed bottom-16 left-3 z-[40] select-none pointer-events-none"
      data-testid="env-badge"
      data-env={envName}
      data-parity={parity}
      data-fe-sha={feShaShort}
      data-be-sha={beShaShort}
    >
      <div className="pointer-events-auto">
      {collapsed ? (
        <button
          type="button"
          onClick={toggleCollapsed}
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900/85 hover:bg-slate-900 text-white text-[10px] px-2.5 py-1 shadow-md backdrop-blur"
          data-testid="env-badge-toggle"
          title={`Build & runtime parity — ${parity}${parityReason ? ' · ' + parityReason : ''}`}
        >
          <Layers className="h-3 w-3" />
          <span className="uppercase tracking-wider font-medium">{envName}</span>
          <span
            className="rounded-sm px-1 font-mono font-semibold"
            style={{ background: palette.bg, color: palette.fg }}
            data-testid="env-badge-parity"
          >
            {parity}
          </span>
          <ChevronUp className="h-3 w-3 opacity-70" />
        </button>
      ) : (
        <div className="rounded-md bg-slate-900/95 text-white shadow-lg backdrop-blur w-[300px] text-[10px] font-mono">
          <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-white/10">
            <span className="inline-flex items-center gap-1 uppercase tracking-wider text-[10px] font-medium">
              <Layers className="h-3 w-3" /> Runtime parity
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { _refreshRuntime(); setRuntime(_readRuntime()); }}
                className="opacity-70 hover:opacity-100"
                data-testid="env-badge-refresh"
                title="Force refresh runtime fingerprint"
              >
                <RefreshCcw className="h-3 w-3" />
              </button>
              <button onClick={toggleCollapsed} className="opacity-70 hover:opacity-100" data-testid="env-badge-collapse" title="Collapse">
                <ChevronDown className="h-3 w-3" />
              </button>
              <button onClick={handleDismiss} className="opacity-70 hover:opacity-100" data-testid="env-badge-dismiss" title="Hide for this session">
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
          <dl className="px-2.5 py-2 space-y-0.5 text-[10px]">
            <Row k="parity" v={parity} testid="env-badge-parity-row" highlight={palette} />
            {parityReason ? (
              <Row k="reason" v={parityReason} testid="env-badge-parity-reason" muted />
            ) : null}
            <Sep />
            <Row k="env" v={envName} testid="env-badge-env" />
            <Row k="fe.sha" v={feShaDisplay} testid="env-badge-fe-sha" />
            <Row k="fe.version" v={feVersionDisplay} testid="env-badge-fe-version" />
            <Row k="fe.built" v={short(FE_BUILD.built_at)} testid="env-badge-fe-built" />
            <Row k="fe.sprint" v={FE_BUILD.sprint || '—'} testid="env-badge-fe-sprint" />
            <Row k="fe.epoch" v={FE_BUILD.runtime_epoch || '—'} testid="env-badge-fe-epoch" />
            <Sep />
            <Row k="be.sha" v={beShaShort} testid="env-badge-be-sha" />
            <Row k="be.version" v={beVersionDisplay} testid="env-badge-be-version" />
            <Row k="be.build" v={beBuildIdDisplay} testid="env-badge-be-build" />
            <Row k="be.boot" v={be?.boot_at_utc ? short(be.boot_at_utc) : '—'} testid="env-badge-be-boot" />
            <Row k="be.startup" v={be?.startup_uuid ? String(be.startup_uuid).slice(0, 12) : '—'} testid="env-badge-be-startup" />
            <Row k="be.host" v={backendHost || '—'} testid="env-badge-be-host" />
            <Sep />
            <Row k="rt.epoch" v={beRuntime?.runtime_epoch || '—'} testid="env-badge-rt-epoch" />
            <Row k="rt.api_compat" v={beRuntime?.api_compat_epoch || '—'} testid="env-badge-rt-api" />
            <Row k="rt.schema" v={beRuntime?.schema_version || '—'} testid="env-badge-rt-schema" />
            <Row k="rt.fp" v={fpHash.slice(0, 16)} testid="env-badge-rt-fp" />
            <Sep />
            <Row k="bundle.sha" v={served?.sha ? String(served.sha).slice(0, 16) : '—'} testid="env-badge-served-sha" />
            <Row k="bundle.src" v={served?.sourceUrl ? hostOf(served.sourceUrl) + new URL(served.sourceUrl).pathname.split('/').pop() : '—'} testid="env-badge-served-src" />
          </dl>
        </div>
      )}
      </div>
    </div>
  );
}

function Row({ k, v, testid, highlight, muted }) {
  return (
    <div className="flex justify-between gap-2">
      <span className={muted ? 'text-white/30' : 'text-white/50'}>{k}</span>
      <span
        className={'truncate max-w-[180px] ' + (muted ? 'text-white/60' : 'text-white')}
        data-testid={testid}
        title={v}
        style={highlight ? {
          background: highlight.bg,
          color: highlight.fg,
          padding: '0 4px',
          borderRadius: 3,
          fontWeight: 600,
        } : undefined}
      >{v}</span>
    </div>
  );
}

function Sep() {
  return <div className="my-1 h-px bg-white/10" />;
}

function short(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toISOString().replace('T', ' ').slice(0, 16) + 'Z';
  } catch (_) { return iso; }
}
